/**
 * Above-the-fold price copy for Round Rock and Spin & Shine.
 * Rates come from the shop catalog and delivery settings, so the page
 * shows what checkout actually charges.
 */

const SHOP_IDS = new Set(['1', '2']);

function num(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

export function formatMoney(value) {
    const n = num(value);
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function formatMiles(value) {
    const n = num(value);
    return Number.isInteger(n) ? String(n) : String(n);
}

function isWeight(service) {
    return service?.inputWeight === true || service?.inputWeight === 'true';
}

function isCustomerService(service) {
    return service?.customerAccess !== false && service?.customerAccess !== 'false';
}

function isSameDay(name) {
    return name.includes('same day') || name.includes('same-day') || name.includes('same-day');
}

function isStandardDropoff(service) {
    const name = String(service?.serviceName || '').toLowerCase();
    if (!isWeight(service) || !name.includes('wash') || !name.includes('fold')) return false;
    if (name.includes('same') || name.includes('weekend')) return false;
    return name.includes('dropoff') || name.includes('drop-off') || name.includes('drop off')
        || name.includes('in store') || name.includes('instore');
}

function dropoffRate(config) {
    const explicit = config?.dropoffWash;
    if (explicit && num(explicit.price) > 0) {
        return {
            price: num(explicit.price),
            minLb: explicit.minBillableWeight != null ? num(explicit.minBillableWeight) : null,
        };
    }
    const rows = (config?.catalogServices || config?.services || config?.laundryServices || []).filter(isStandardDropoff);
    if (!rows.length) return null;
    const cheapest = rows.reduce((best, service) => (num(service.price) < num(best.price) ? service : best));
    return {
        price: num(cheapest.price),
        minLb: cheapest.minBillableWeight != null ? num(cheapest.minBillableWeight) : null,
    };
}

export function washFoldRates(services) {
    const rows = (services || []).filter((service) => {
        const name = String(service.serviceName || '').toLowerCase();
        return isWeight(service) && isCustomerService(service) && name.includes('wash') && name.includes('fold');
    });
    const standard = rows.filter((service) => !isSameDay(String(service.serviceName || '').toLowerCase()));
    const pool = standard.length ? standard : rows;
    if (!pool.length) return null;
    const cheapest = pool.reduce((best, service) => (num(service.price) < num(best.price) ? service : best));
    const sameDay = rows.find((service) => isSameDay(String(service.serviceName || '').toLowerCase()));
    return {
        price: num(cheapest.price),
        minLb: cheapest.minBillableWeight != null ? num(cheapest.minBillableWeight) : null,
        sameDayPrice: sameDay ? num(sameDay.price) : null,
    };
}

function normalizeTiers(tiers) {
    if (!Array.isArray(tiers)) return [];
    const cleaned = tiers.filter((tier) => tier && typeof tier === 'object').map((tier) => {
        const raw = tier.up_to_mi ?? tier.upToMi ?? tier.up_to;
        let upTo = null;
        if (raw !== null && raw !== undefined && raw !== '' && raw !== 'null') {
            const value = Number(raw);
            upTo = Number.isFinite(value) && value > 0 ? value : null;
        }
        return {
            upTo,
            flat: num(tier.flat),
            perMile: num(tier.per_mile_over ?? tier.perMileOver),
        };
    });
    cleaned.sort((a, b) => (a.upTo == null) - (b.upTo == null) || ((a.upTo || 0) - (b.upTo || 0)));
    return cleaned;
}

function tierFee(tier) {
    if (tier.flat === 0 && tier.perMile === 0) return 'Free';
    if (tier.perMile > 0) return `${formatMoney(tier.flat)} plus ${formatMoney(tier.perMile)} per mile`;
    return formatMoney(tier.flat);
}

export function deliveryLines(config) {
    const mode = config?.deliveryFeeMode || config?.delivery_fee_mode || 'none';
    if (mode === 'tiered') {
        const tiers = normalizeTiers(config.deliveryFeeTiers || config.delivery_fee_tiers);
        if (!tiers.length) return ['Pickup & delivery available'];
        let prev = 0;
        return tiers.map((tier) => {
            const fee = tierFee(tier);
            let line;
            if (tier.upTo == null) line = `${fee} after ${formatMiles(prev)} miles`;
            else if (prev === 0) line = `${fee} within ${formatMiles(tier.upTo)} miles`;
            else line = `${fee} for ${formatMiles(prev)}–${formatMiles(tier.upTo)} miles`;
            if (tier.upTo != null) prev = tier.upTo;
            return line;
        });
    }
    if (mode === 'flat') {
        const flat = num(config.deliveryFeeFlat ?? config.delivery_fee_flat);
        return [flat > 0 ? `Pickup & delivery ${formatMoney(flat)}` : 'Pickup & delivery included'];
    }
    if (mode === 'distance') {
        const free = num(config.deliveryFeeFreeRadiusMi ?? config.delivery_fee_free_radius_mi);
        const per = num(config.deliveryFeePerMile ?? config.delivery_fee_per_mile);
        const base = num(config.deliveryFeeBase ?? config.delivery_fee_base);
        const lines = [];
        if (free > 0) lines.push(`Free within ${formatMiles(free)} miles`);
        if (base > 0) lines.push(`${formatMoney(base)} to start`);
        if (per > 0) lines.push(`${formatMoney(per)} per mile${free > 0 ? ' after that' : ''}`);
        return lines.length ? lines : ['Pickup & delivery available'];
    }
    return ['Pickup & delivery available'];
}

function hoursBlob(siteContent) {
    const hours = Array.isArray(siteContent?.hours) ? siteContent.hours : [];
    return [siteContent?.hoursNote || '', ...hours.map((row) => `${row.day || row.label || ''} ${row.time || ''}`)].join(' ');
}

export function isOpenAllDay(siteContent) {
    return /24\s*\/\s*7|24 hours|open 24/i.test(hoursBlob(siteContent));
}

export function hoursLabel(siteContent) {
    if (isOpenAllDay(siteContent)) return 'Open 24/7';
    const hours = Array.isArray(siteContent?.hours) ? siteContent.hours : [];
    const first = hours.find((row) => row.time);
    if (!first) return '';
    return [first.day || first.label || '', first.time].filter(Boolean).join(' ');
}

function pickupSummary(delivery) {
    const first = delivery[0] || 'Pickup & delivery available';
    if (first.toLowerCase().startsWith('pickup')) return first;
    return `Pickup & delivery: ${first.charAt(0).toLowerCase()}${first.slice(1)}`;
}

/**
 * Offer block for laundry 1 and 2. Null for every other shop, and when
 * that shop has no per-pound wash-and-fold price.
 */
export function priceOffer(config) {
    const id = String(config?.laundryId || '');
    if (!SHOP_IDS.has(id)) return null;
    const rate = washFoldRates(config?.services || config?.laundryServices);
    const dropoff = dropoffRate(config);
    if ((!rate || rate.price <= 0) && (!dropoff || dropoff.price <= 0)) return null;

    const siteContent = config?.siteContent || {};
    const city = String(siteContent.city || '').trim();
    const open = isOpenAllDay(siteContent);
    const lead = dropoff && dropoff.price > 0 ? dropoff : rate;
    const priceText = `${formatMoney(lead.price)}/lb`;
    const headlineLead = `${open ? '24/7 ' : ''}${dropoff ? 'Drop-off wash & fold' : 'Wash & Fold'}${city ? ` in ${city}` : ''} — `;
    const delivery = deliveryLines(config);
    const summary = pickupSummary(delivery);
    const promoOn = siteContent.promoEnabled !== false && siteContent.promoDiscount;
    const promoLine = promoOn ? `${siteContent.promoDiscount}% off your first order` : '';
    const code = promoOn && siteContent.promoCode ? String(siteContent.promoCode) : '';
    const recurring = num(config?.recurringDiscount);
    const showMin = config?.minWeightEnabled === true && rate && rate.minLb > 0;
    const showDropMin = config?.minWeightEnabled === true && dropoff && dropoff.minLb > 0;
    const hoursLine = open ? '24/7 drop-off' : (hoursLabel(siteContent) ? `Store hours ${hoursLabel(siteContent)}` : '');
    const pickupPrice = rate && rate.price > 0 ? `Pickup & delivery ${formatMoney(rate.price)}/lb` : '';

    const lines = [
        dropoff ? `Drop-off wash & fold ${formatMoney(dropoff.price)}/lb` : `Wash & Fold from ${priceText}`,
        showDropMin ? `Drop-off ${formatMiles(dropoff.minLb)} lb minimum` : '',
        pickupPrice,
        showMin ? `Pickup ${formatMiles(rate.minLb)} lb minimum` : '',
        rate && rate.sameDayPrice > 0 ? `Same-day pickup ${formatMoney(rate.sameDayPrice)}/lb` : '',
        ...(pickupPrice && summary === 'Pickup & delivery available'
            ? []
            : [summary.startsWith('Pickup & delivery: ') ? `Delivery fee: ${summary.slice('Pickup & delivery: '.length)}` : summary, ...delivery.slice(1)]),
        recurring > 0 ? `Recurring pickups: ${Number.isInteger(recurring) ? recurring : recurring}% off` : '',
        promoLine,
        code ? `Code ${code}` : '',
        hoursLine,
        'Easy online scheduling',
    ].filter(Boolean);

    const highlights = dropoff
        ? [
            `Drop-off wash & fold ${formatMoney(dropoff.price)}/lb`,
            pickupPrice || summary,
            promoLine || 'Easy online scheduling',
        ]
        : [
            `Wash & Fold from ${priceText}`,
            summary,
            promoLine || 'Easy online scheduling',
        ];

    return {
        headlineLead,
        priceText,
        subheadline: [
            pickupPrice || 'Pickup & delivery available',
            promoLine ? `${siteContent.promoDiscount}% off first order` : '',
        ].filter(Boolean).join(' • '),
        highlights,
        lines,
        badges: [
            summary,
            open ? 'Open 24/7' : (hoursLabel(siteContent) || 'Easy online scheduling'),
            'Easy online scheduling',
        ],
        code,
    };
}
